import React, { useState } from "react";
import LocationSearch from "../../widgets/LocationSearch";
import ImageUpload from "../../widgets/ImageUpload";
import UsernameField from "../../widgets/UsernameField";
import CreatePasswordField from "../../widgets/CreatePasswordField";
import { Textarea } from "../ui/textarea";
import PhoneInput from "../ui/PhoneInput";
import { isValidE164 } from "../ui/phoneUtils";
import { cn } from "../../lib/utils";

export default function OrgInfo({ formData, onFormDataUpdate, onBack, onNext }) {
    const [errors, setErrors] = useState({});
    const [usernameValid, setUsernameValid] = useState(false);
    const [passwordValid, setPasswordValid] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        onFormDataUpdate({ [name]: value });
        // Clear error for this field when user starts typing
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: "" }));
        }
    };

    const addLink = () => {
        const currentLinks = formData.links || [];
        onFormDataUpdate({
            links: [...currentLinks, { label: "", url: "" }]
        });
    };

    const removeLink = (index) => {
        const currentLinks = formData.links || [];
        onFormDataUpdate({
            links: currentLinks.filter((_, i) => i !== index)
        });
    };

    const updateLink = (index, field, value) => {
        const currentLinks = [...(formData.links || [])];
        currentLinks[index] = { ...currentLinks[index], [field]: value };
        onFormDataUpdate({ links: currentLinks });
    };

    const validateForm = () => {
        const newErrors = {};
        
        if (!formData.name || formData.name.trim() === "") {
            newErrors.name = "Organization name is required";
        }
        if (!formData.address || formData.address.trim() === "") {
            newErrors.address = "Address is required";
        }
        if (!formData.website || formData.website.trim() === "") {
            newErrors.website = "Website is required";
        }
        if (!formData.username || formData.username.trim() === "") {
            newErrors.username = "Username is required";
        } else if (!usernameValid) {
            newErrors.username = "Choose an available username";
        }
        if (!formData.email || formData.email.trim() === "") {
            newErrors.email = "Email is required";
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = "Please enter a valid email address";
        }
        if (!passwordValid) {
            newErrors.password = "Password does not meet the requirements";
        }
        if (!formData.mission_statement || formData.mission_statement.trim() === "") {
            newErrors.mission_statement = "Mission statement is required";
        }
        if (formData.phone && formData.phone.trim() && !isValidE164(formData.phone.trim())) {
            newErrors.phone = "Please enter a valid phone number";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleNextClick = () => {
        if (validateForm()) {
            onNext();
        }
    };

    return (
        <div className="space-y-6">
            <h2 className="text-2xl font-bold text-text-primary mb-4">Organization Information</h2>
            
            <div className="space-y-6">
                {/* Logo */}
                <div className="rounded-lg border border-border p-4">
                    <ImageUpload
                        value={formData.pfp_url || ""}
                        onChange={(imageData) => {
                            onFormDataUpdate({
                                pfp_url: imageData.url || "",
                                pfp_key: imageData.key || "",
                            });
                        }}
                        label="Organization logo"
                    />
                    <p className="mt-2 text-xs text-text-muted">
                        Optional. You can also add or change this later from your organization profile.
                    </p>
                </div>

                {/* Required Fields */}
                <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-text-primary">Required Information</h3>
                    
                    {/* Organization Name */}
                    <div>
                        <label className="block text-sm font-medium text-text-secondary mb-1">
                            Organization Name <span className="text-error">*</span>
                        </label>
                        <input
                            type="text"
                            name="name"
                            value={formData.name || ""}
                            onChange={handleChange}
                            required
                            className={`bg-secondary text-text-primary w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-accent ${
                                errors.name ? "border-error" : "border-border"
                            }`}
                            placeholder="Enter organization name"
                        />
                        {errors.name && <p className="text-error text-sm mt-1">{errors.name}</p>}
                    </div>

                    {/* Email */}
                    <div>
                        <label className="block text-sm font-medium text-text-secondary mb-1">
                            Email <span className="text-error">*</span>
                        </label>
                        <input
                            type="email"
                            name="email"
                            value={formData.email || ""}
                            onChange={handleChange}
                            required
                            className={`bg-secondary text-text-primary w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-accent ${
                                errors.email ? "border-error" : "border-border"
                            }`}
                            placeholder="contact@organization.com"
                        />
                        {errors.email && <p className="text-error text-sm mt-1">{errors.email}</p>}
                    </div>

                    {/* Phone */}
                    <div>
                        <label className="block text-sm font-medium text-text-secondary mb-1" htmlFor="org-phone">
                            Phone
                        </label>
                        <PhoneInput
                            id="org-phone"
                            value={formData.phone || ""}
                            invalid={Boolean(errors.phone)}
                            onChange={(phone) => {
                                onFormDataUpdate({ phone });
                                if (errors.phone) {
                                    setErrors((prev) => ({ ...prev, phone: "" }));
                                }
                            }}
                        />
                        {errors.phone && <p className="text-error text-sm mt-1">{errors.phone}</p>}
                    </div>

                    <UsernameField
                        id="org-username"
                        label="Username"
                        value={formData.username || ""}
                        onChange={(username) => {
                            setUsernameValid(false);
                            onFormDataUpdate({ username });
                            if (errors.username) {
                                setErrors((prev) => ({ ...prev, username: "" }));
                            }
                        }}
                        onValidityChange={setUsernameValid}
                        inputClassName="h-10 bg-secondary"
                    />
                    {errors.username && !usernameValid ? (
                        <p className="text-error text-sm -mt-2">{errors.username}</p>
                    ) : null}

                    <CreatePasswordField
                        id="org-password"
                        value={formData.password || ""}
                        onChange={(password) => {
                            onFormDataUpdate({ password });
                            if (errors.password) {
                                setErrors((prev) => ({ ...prev, password: "" }));
                            }
                        }}
                        showConfirm
                        confirmValue={formData.confirm_password || ""}
                        onConfirmChange={(confirm_password) => {
                            onFormDataUpdate({ confirm_password });
                            if (errors.confirm_password) {
                                setErrors((prev) => ({ ...prev, confirm_password: "" }));
                            }
                        }}
                        onValidityChange={setPasswordValid}
                        inputClassName="h-10 bg-secondary"
                    />
                    {errors.password && !passwordValid ? (
                        <p className="text-error text-sm -mt-2">{errors.password}</p>
                    ) : null}

                    {/* Address */}
                    <div>
                        <label className="block text-sm font-medium text-text-secondary mb-1">
                            Address <span className="text-error">*</span>
                        </label>
                        <LocationSearch
                            value={{
                                value: formData.address || "",
                                lat: formData.latitude,
                                lon: formData.longitude
                            }}
                            onChange={(locationData) => {
                                onFormDataUpdate({
                                    address: locationData.value,
                                    latitude: locationData.lat,
                                    longitude: locationData.lon
                                });
                            }}
                            placeholder="Search for an address..."
                            searchType="address"
                        />
                        {errors.address && <p className="text-error text-sm mt-1">{errors.address}</p>}
                    </div>

                    {/* Website */}
                    <div>
                        <label className="block text-sm font-medium text-text-secondary mb-1">
                            Website <span className="text-error">*</span>
                        </label>
                        <input
                            type="url"
                            name="website"
                            value={formData.website || ""}
                            onChange={handleChange}
                            required
                            className={`bg-secondary text-text-primary w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-accent ${
                                errors.website ? "border-error" : "border-border"
                            }`}
                            placeholder="https://www.organization.com"
                        />
                    </div>

                    {/* Mission Statement */}
                    <div>
                        <label className="block text-sm font-medium text-text-secondary mb-1">
                            Mission Statement <span className="text-error">*</span>
                        </label>
                        <Textarea
                            name="mission_statement"
                            required
                            rows={3}
                            value={formData.mission_statement || ""}
                            onChange={handleChange}
                            className={cn(
                              "bg-secondary",
                              errors.mission_statement ? "border-error" : "border-border"
                            )}
                            placeholder="Share your organization's mission statement..."
                        />
                        {errors.mission_statement && <p className="text-error text-sm mt-1">{errors.mission_statement}</p>}
                    </div>
                </div>

                {/* Optional Fields */}
                <div className="space-y-4 pt-4 border-t border-border">
                    {/* <h3 className="text-lg font-semibold text-text-primary">Optional Information</h3> */}

                    {/* Links Section */}
                    <div>
                        <div className="flex items-center justify-between mb-3">
                            <label className="block text-sm font-medium text-text-secondary">
                                Links (YouTube, Social Media, etc.)
                            </label>
                            <button
                                type="button"
                                onClick={addLink}
                                className="text-sm bg-secondary text-accent border border-accent px-3 py-1 rounded-md hover:bg-secondary transition"
                            >
                                + Add Link
                            </button>
                        </div>
                        
                        {formData.links && Array.isArray(formData.links) && formData.links.length > 0 ? (
                            <div className="space-y-3">
                                {formData.links.map((link, index) => (
                                    <div key={index} className="flex gap-3 items-start">
                                        <div className="flex-1">
                                            <input
                                                type="text"
                                                placeholder="Link label (e.g., Website, YouTube, LinkedIn)"
                                                value={link.label || ""}
                                                onChange={(e) => updateLink(index, 'label', e.target.value)}
                                                className="bg-secondary text-text-primary w-full border border-border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-accent mb-2"
                                            />
                                            <input
                                                type="url"
                                                placeholder="https://example.com"
                                                value={link.url || ""}
                                                onChange={(e) => updateLink(index, 'url', e.target.value)}
                                                className="bg-secondary text-text-primary w-full border border-border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-accent"
                                            />
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => removeLink(index)}
                                            className="text-error hover:text-error p-2 transition"
                                            title="Remove link"
                                        >
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                            </svg>
                                        </button>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-text-secondary text-sm">No links added yet. Click "Add Link" to get started.</p>
                        )}
                    </div>
                </div>
            </div>

            {/* Navigation Buttons */}
            <div className="flex justify-between pt-6 border-t border-border">
                <button
                    onClick={onBack}
                    className="px-6 py-2 bg-secondary text-text-primary border border-border rounded-md hover:bg-tertiary transition"
                >
                    Back
                </button>
                <button
                    onClick={handleNextClick}
                    disabled={!usernameValid || !passwordValid}
                    className="px-6 py-2 bg-accent text-text-on-accent rounded-md hover:bg-accent-hover transition disabled:pointer-events-none disabled:opacity-50"
                >
                    Next
                </button>
            </div>
        </div>
    );
}

